import { OperatorTypeEnum } from 'types/tableTypes'
import { normalizeAppealStatusValues } from 'features/claims/claimsApi'
import { subtractDaysWindowLocal } from './dateUtils'
import type { FieldIds, FilterFormState } from './filterTypes'

/**
 * AG Grid Filter Model Builders
 * These functions create and extract AG Grid compatible filter models.
 */

/* Basic filter factories  */

type AgTextFilter = {
  filterType: 'text'
  type: 'startsWith' | 'endsWith' | 'contains'
  filter: string
}

type AgSetFilter = {
  filterType: 'set'
  values: string[]
}

type AgDateFilter =
  | { filterType: 'date'; type: 'inRange'; dateFrom: string; dateTo: string }
  | { filterType: 'date'; type: 'equals'; dateFrom: string }
  | { filterType: 'date'; type: OperatorTypeEnum.greaterThan; dateFrom: string }
  | { filterType: 'date'; type: OperatorTypeEnum.lessThan; dateFrom: string }

type AgFilter = AgTextFilter | AgSetFilter | AgDateFilter | (AgDateFilter & { _metaData?: any })
type BatchContextFieldIds = Pick<FieldIds, 'batchCode' | 'oaiWorked'>

/**
 * Builds a rolling "Last N days" date filter for oaiWorked.
 */
export const buildRollingDateFilter = (days: number) => {
  const r = subtractDaysWindowLocal(days)
  return {
    filterType: 'date' as const,
    type: 'inRange' as const,
    dateFrom: r.from,
    dateTo: r.to,
    _metaData: { preset: days },
  }
}

/**
 * Batch navigation should ignore previously persisted claim filters and
 * start with only the selected batch plus the standard last-modified window.
 */
export const buildBatchContextFilterModel = (
  batchCode: string,
  batchId: string | undefined,
  fields: BatchContextFieldIds,
) => {
  const oaiFilter = buildRollingDateFilter(365)

  return {
    [fields.batchCode]: {
      filterType: 'text',
      type: 'contains',
      filter: batchCode,
      _metaData: { batchId },
    },
    [fields.oaiWorked]: oaiFilter,
  }
}

/** Text filter with 'contains' type */
export const createTextFilter = (value: string): AgTextFilter | null =>
  value ? { filterType: 'text', type: 'startsWith', filter: value } : null

/** Set filter (for dropdown selections) */
export const createSetFilter = (values: string | string[]): AgSetFilter | null => {
  const arr = Array.isArray(values) ? values : [values]
  const hasValue = arr.length > 0 && !!arr[0]
  return hasValue ? { filterType: 'set', values: arr } : null
}

/** Date range filter (YYYY-MM-DD) */
export const createDateRangeFilter = (
  dateFrom?: string,
  dateTo?: string,
  operator?: 'range' | 'less-than' | 'greater-than',
): AgDateFilter | null => {
  const mode = operator || (dateFrom && dateTo ? 'range' : dateFrom ? 'greater-than' : 'less-than')

  if (mode === 'range' && dateFrom && dateTo) {
    return { filterType: 'date', type: 'inRange', dateFrom, dateTo }
  }
  if (mode === 'greater-than' && dateFrom) {
    return { filterType: 'date', type: OperatorTypeEnum.greaterThan, dateFrom }
  }
  if (mode === 'less-than' && dateTo) {
    return { filterType: 'date', type: OperatorTypeEnum.lessThan, dateFrom: dateTo }
  }
  return null
}

/* Model builders */

const addFilterIfPresent = (model: Record<string, AgFilter>, fieldId: string, filter: AgFilter | null) => {
  if (filter) {
    model[fieldId] = filter
  }
}

/**
 * Builds the complete AG Grid filter model from form state.
 * - Text filters use `contains`
 * - Dropdowns use set filters
 * - Date fields use date filters
 * - `lastModQuickModel` overrides manual OAI Worked date fields
 */
export const buildFilterModel = (
  form: FilterFormState,
  fields: FieldIds,
  lastModQuickModel: any | null,
): Record<string, AgFilter> => {
  const filterModel: Record<string, AgFilter> = {}

  // Text filters
  addFilterIfPresent(filterModel, fields.batchCode, createTextFilter(form.batchCode))
  addFilterIfPresent(filterModel, fields.patientLastName, createTextFilter(form.patientLastName))
  addFilterIfPresent(filterModel, fields.memberId, createTextFilter(form.memberId))
  addFilterIfPresent(filterModel, fields.visitId, createTextFilter(form.visitId))
  addFilterIfPresent(filterModel, fields.uniqueIdentifier, createTextFilter(form.uniqueIdentifier))

  // Set filters (dropdowns)
  addFilterIfPresent(filterModel, fields.claimStatusPayer, createSetFilter(form.claimStatusPayer))
  addFilterIfPresent(filterModel, fields.appealStatus, createSetFilter(form.appealStatus))
  addFilterIfPresent(filterModel, fields.owner, createSetFilter(form.owner))

  // Admit date range
  addFilterIfPresent(
    filterModel,
    fields.admitDate,
    createDateRangeFilter(form.admitDateFrom, form.admitDateTo, form.admitDateOperator),
  )

  // OAI Worked (Last Modified)
  // 1) If quick filter is used -> trust `lastModQuickModel`
  if (lastModQuickModel) {
    addFilterIfPresent(filterModel, fields.oaiWorked, lastModQuickModel as AgFilter)
  } else if (form.oaiWorkedFrom || form.oaiWorkedTo) {
    // 2) Otherwise, use manual date range and mark as customRange
    const dateFilter = createDateRangeFilter(form.oaiWorkedFrom, form.oaiWorkedTo, form.oaiWorkedOperator)
    if (dateFilter) {
      addFilterIfPresent(filterModel, fields.oaiWorked, {
        ...dateFilter,
        _metaData: { customRange: true },
      } as AgFilter)
    }
  }
  return filterModel
}

/* Extraction helpers  */

const getTextFilterValue = (filterModel: any, fieldId: string): string => {
  const filter = filterModel?.[fieldId]
  return filter?.filter ?? ''
}

const getSetFilterValues = (filterModel: any, fieldId: string): string[] => {
  console.log(filterModel)
  const filter = filterModel?.[fieldId]
  return Array.isArray(filter?.values) ? filter.values : []
}

const getDateRange = (
  filterModel: any,
  fieldId: string,
): { from: string; to: string; operator?: 'range' | 'less-than' | 'greater-than' } => {
  const filter = filterModel?.[fieldId]

  let from = ''
  let to = ''
  let operator: 'range' | 'less-than' | 'greater-than' | undefined

  if (filter?.type === 'inRange') {
    operator = 'range'
    from = filter?.dateFrom ?? ''
    to = filter?.dateTo ?? ''
  } else if (filter?.type === OperatorTypeEnum.greaterThan) {
    operator = 'greater-than'
    from = filter?.dateFrom ?? ''
    to = ''
  } else if (filter?.type === OperatorTypeEnum.lessThan) {
    operator = 'less-than'
    from = ''
    // AG Grid stores lessThan date in dateFrom field, but it represents our 'to' value
    to = filter?.dateFrom ?? ''
  }

  return { from, to, operator }
}

/**
 * Extracts form values from an AG Grid filter model.
 * Used to restore filter state from saved preferences.
 */
export const extractFormFromFilterModel = (
  filterModel: any,
  fields: FieldIds,
  lastModQuickHasModel: boolean,
): FilterFormState => {
  const initial: FilterFormState = {
    batchCode: '',
    claimStatusPayer: [],
    appealStatus: [],
    owner: [],
    patientLastName: '',
    memberId: '',
    visitId: '',
    uniqueIdentifier: '',
    admitDateFrom: '',
    admitDateTo: '',
    admitDateOperator: undefined,
    oaiWorkedFrom: '',
    oaiWorkedTo: '',
    oaiWorkedOperator: undefined,
  }

  if (!filterModel) return initial

  // Text filters
  const batchCode = getTextFilterValue(filterModel, fields.batchCode)
  const patientLastName = getTextFilterValue(filterModel, fields.patientLastName)
  const memberId = getTextFilterValue(filterModel, fields.memberId)
  const visitId = getTextFilterValue(filterModel, fields.visitId)
  const uniqueIdentifier = getTextFilterValue(filterModel, fields.uniqueIdentifier)

  // Set filters
  const claimStatusPayer = getSetFilterValues(filterModel, fields.claimStatusPayer)
  const appealStatus = normalizeAppealStatusValues(getSetFilterValues(filterModel, fields.appealStatus))
  const owner = getSetFilterValues(filterModel, fields.owner)

  // Admit date range
  const {
    from: admitDateFrom,
    to: admitDateTo,
    operator: admitDateOperator,
  } = getDateRange(filterModel, fields.admitDate)

  let oaiWorkedFrom = ''
  let oaiWorkedTo = ''
  let oaiWorkedOperator: 'range' | 'less-than' | 'greater-than' | undefined

  if (!lastModQuickHasModel) {
    const oai = getDateRange(filterModel, fields.oaiWorked)
    oaiWorkedFrom = oai.from
    oaiWorkedTo = oai.to
    oaiWorkedOperator = oai.operator
  }

  return {
    batchCode,
    claimStatusPayer,
    appealStatus,
    owner,
    patientLastName,
    memberId,
    visitId,
    uniqueIdentifier,
    admitDateFrom,
    admitDateTo,
    admitDateOperator,
    oaiWorkedFrom,
    oaiWorkedTo,
    oaiWorkedOperator,
  }
}
